import { useState } from 'react'
import { Play, Loader2, AlertTriangle, Shield, Fingerprint, CreditCard, ChevronDown, ChevronRight, Building2, User, CheckCircle2 } from 'lucide-react'
import { SANDBOX_ENTITIES, type SandboxEntity } from './data'
import { showToast } from '@store/uiStore'
import { sandboxSeedApi } from '@api/client'

const FLAG_STYLES: Record<string, { bg: string; text: string }> = {
  PEP: { bg: 'bg-amber-500/10', text: 'text-amber-400' },
  Sanction: { bg: 'bg-red-500/10', text: 'text-red-400' },
  Fraud: { bg: 'bg-rose-500/10', text: 'text-rose-400' },
}

function FlagBadge({ flag }: { flag: string }) {
  const style = FLAG_STYLES[flag] || { bg: 'bg-gray-500/10', text: 'text-gray-400' }
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase ${style.bg} ${style.text}`}>
      {flag === 'PEP' && <Shield size={10} />}
      {flag === 'Sanction' && <AlertTriangle size={10} />}
      {flag === 'Fraud' && <Fingerprint size={10} />}
      {flag}
    </span>
  )
}

function DocBadge({ doc }: { doc: string }) {
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-medium bg-[var(--bg-tertiary)] text-[var(--text-muted)] border border-[var(--border-color)]">
      <CreditCard size={10} />
      {doc}
    </span>
  )
}

function EntityRow({ entity, expanded, onToggle }: {
  entity: SandboxEntity
  expanded: boolean
  onToggle: () => void
}) {
  return (
    <div className="border border-[var(--border-color)] rounded-lg overflow-hidden">
      <button
        onClick={onToggle}
        className="w-full flex items-center gap-3 p-3 text-left hover:bg-[var(--bg-tertiary)] transition-colors"
      >
        <div className="shrink-0 text-[var(--text-muted)]">
          {expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            {entity.type === 'organization' ? (
              <Building2 size={14} className="text-blue-400 shrink-0" />
            ) : (
              <User size={14} className="text-[var(--text-muted)] shrink-0" />
            )}
            <span className="text-sm font-semibold text-[var(--text-primary)]">{entity.label}</span>
            {entity.flags.map(f => <FlagBadge key={f} flag={f} />)}
          </div>
          <p className="text-xs text-[var(--text-muted)] mt-0.5 truncate">{entity.description}</p>
        </div>
      </button>

      {expanded && (
        <div className="px-4 pb-3 pt-1 border-t border-[var(--border-color)] bg-[var(--bg-primary)]">
          <div className="space-y-2">
            <div>
              <span className="text-[10px] uppercase font-semibold text-[var(--text-muted)] tracking-wider">
                {entity.type === 'organization' ? 'Registration' : 'Documents'}
              </span>
              <div className="flex flex-wrap gap-1.5 mt-1">
                {entity.documents.map(d => <DocBadge key={d} doc={d} />)}
              </div>
            </div>

            <div>
              <span className="text-[10px] uppercase font-semibold text-[var(--text-muted)] tracking-wider">Expected Result</span>
              <p className="text-xs font-mono text-[var(--text-primary)] mt-0.5">{entity.expectedResult}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

type AccountType = 'presales' | 'sales-demo-v2' | 'sales-demo-v1'

function SeedButton({ account, label, seeding, onSeed }: {
  account: AccountType
  label: string
  seeding: AccountType | null
  onSeed: (account: AccountType) => void
}) {
  const isSeeding = seeding === account
  const isDisabled = seeding !== null

  return (
    <button
      onClick={() => onSeed(account)}
      disabled={isDisabled}
      className="shrink-0 flex items-center gap-2 px-4 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-semibold transition-colors"
    >
      {isSeeding ? (
        <>
          <Loader2 size={16} className="animate-spin" />
          Seeding...
        </>
      ) : (
        <>
          <Play size={16} />
          {label}
        </>
      )}
    </button>
  )
}

export function SandboxTool() {
  const [seeding, setSeeding] = useState<AccountType | null>(null)
  const [lastSeeded, setLastSeeded] = useState<AccountType | null>(null)
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set())

  const toggleExpanded = (id: string) => {
    setExpandedIds(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const handleSeed = async (account: AccountType) => {
    setSeeding(account)
    setLastSeeded(null)

    try {
      const result = account === 'sales-demo-v2'
        ? await sandboxSeedApi.triggerSeedV2()
        : account === 'sales-demo-v1'
          ? await sandboxSeedApi.triggerSeedV1()
          : await sandboxSeedApi.triggerSeed()

      if (result.success) {
        setLastSeeded(account)
        const data = result.data as any
        showToast(data?.message || 'Sandbox entities seeded successfully', 'success')
      } else {
        showToast(result.error || 'Failed to seed entities', 'error')
      }
    } catch (err) {
      showToast(`Network error: ${err instanceof Error ? err.message : String(err)}`, 'error')
    } finally {
      setSeeding(null)
    }
  }

  const accountLabel = lastSeeded === 'sales-demo-v2' ? 'Sales Demo v2'
    : lastSeeded === 'sales-demo-v1' ? 'Sales Demo v1'
    : 'PreSales Sandbox'

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-lg font-semibold text-[var(--text-primary)]">Sandbox Entity Seeder</h2>
          <p className="text-sm text-[var(--text-muted)] mt-1">
            Create pre-configured test entities in FrankieOne sandbox accounts.
            Each entity demonstrates different verification outcomes — PEP matches, sanctions, fraud flags, visa checks, and clean passes.
          </p>
          <p className="text-xs text-[var(--text-muted)] mt-1">
            Also runs automatically every day at 1am AEST via GitHub Actions.
          </p>
        </div>

        <div className="flex flex-col gap-2">
          <SeedButton account="presales" label="Seed PreSales" seeding={seeding} onSeed={handleSeed} />
          <SeedButton account="sales-demo-v2" label="Seed Demo v2" seeding={seeding} onSeed={handleSeed} />
          <SeedButton account="sales-demo-v1" label="Seed Demo v1" seeding={seeding} onSeed={handleSeed} />
        </div>
      </div>

      {/* Triggered Banner */}
      {lastSeeded && (
        <div className="flex items-center gap-3 px-3 py-2 rounded-lg bg-emerald-500/10 border border-emerald-500/20">
          <CheckCircle2 size={16} className="text-emerald-400 shrink-0" />
          <span className="text-sm text-emerald-400">{accountLabel} entities seeded successfully. Check the sandbox portal to verify.</span>
        </div>
      )}

      {/* Individual Entities */}
      <div className="space-y-2">
        <h3 className="text-sm font-semibold text-[var(--text-primary)]">
          Individuals ({SANDBOX_ENTITIES.filter(e => e.type === 'individual').length})
        </h3>
        {SANDBOX_ENTITIES.filter(e => e.type === 'individual').map(entity => (
          <EntityRow
            key={entity.id}
            entity={entity}
            expanded={expandedIds.has(entity.id)}
            onToggle={() => toggleExpanded(entity.id)}
          />
        ))}
      </div>

      {/* Business Entities */}
      <div className="space-y-2">
        <h3 className="text-sm font-semibold text-[var(--text-primary)]">
          Businesses ({SANDBOX_ENTITIES.filter(e => e.type === 'organization').length})
        </h3>
        {SANDBOX_ENTITIES.filter(e => e.type === 'organization').map(entity => (
          <EntityRow
            key={entity.id}
            entity={entity}
            expanded={expandedIds.has(entity.id)}
            onToggle={() => toggleExpanded(entity.id)}
          />
        ))}
      </div>
    </div>
  )
}
